import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import React from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  CUSTOMER_CARE_HOURS,
  CUSTOMER_CARE_OPTION,
  CUSTOMER_CARE_PHONE,
  CUSTOMER_CARE_PHONE_HREF,
} from "constant/patientPortalConstants";

// The Customer Care number, tappable to call, with its option and hours.
const CustomerCareLine = () => (
  <>
    <Link href={CUSTOMER_CARE_PHONE_HREF} color="inherit" sx={{ fontWeight: 500 }}>
      {CUSTOMER_CARE_PHONE}
    </Link>
    , {CUSTOMER_CARE_OPTION}, {CUSTOMER_CARE_HOURS}
  </>
);

interface props {
  from?: string;
  date?: string;
  syncStatus?: boolean;
  displayBanner?: boolean;
  BackEndError?: boolean;
  syncActionError?: boolean;
  syncActionMsg?: string;
  syncActionDate?: string;
  // Opens the contact form in place; without it, "Contact us" navigates Home.
  onContactUs?: () => void;
}

const ClientRescheduleError: React.FC<props> = ({
  from,
  date,
  syncStatus,
  displayBanner,
  BackEndError,
  syncActionError,
  syncActionMsg,
  syncActionDate,
  onContactUs,
}) => {
  const startDate = moment(date).format("h:mm a");
  const syncActionDateFormat = moment(syncActionDate).format("h:mm a");
  const navigate = useNavigate();

  const shouldDisplayErrorMessage =
    displayBanner ||
    BackEndError ||
    (syncStatus === true && from === "cancelFlow") ||
    (syncActionError && syncActionMsg === "cancel") ||
    (syncStatus === true && from === "rescheduleFlow") ||
    (syncActionError && syncActionMsg === "update");

  if (!shouldDisplayErrorMessage) {
    return null;
  }

  const handleGoBack = () => {
    if (onContactUs) {
      onContactUs();
      return;
    }
    navigate("/appointments", {
      state: {
        scrollToForm: true,
      },
    });
  };

  if (displayBanner || BackEndError) {
    return (
      <Alert severity="warning" role="alert">
        We’re temporarily unable to change your appointments through the portal
        right now. Please call our Customer Care team at <CustomerCareLine />.
        Outside of business hours,{" "}
        <Link component="button" color="inherit" onClick={handleGoBack} sx={{ verticalAlign: "baseline", fontWeight: 500 }}>
          Contact us
        </Link>{" "}
        and we'll handle it first thing in the morning
      </Alert>
    );
  }

  if (
    (syncStatus === true && from === "cancelFlow") ||
    (syncActionError && syncActionMsg === "cancel")
  ) {
    return (
      <Alert severity="error" role="alert">
        Due to a system error we could not cancel your appointment at{" "}
        {syncActionError && syncActionMsg === "cancel"
          ? syncActionDateFormat
          : startDate}
        . Please call our Customer Care team at <CustomerCareLine />. Outside
        of business hours, email us and we’ll handle it first thing in the
        morning.
      </Alert>
    );
  }

  return (
    <Alert severity="error" role="alert">
      Editing appointments online is currently unavailable due to a system
      error. Please call the Contact Center at <CustomerCareLine />.
    </Alert>
  );
};

export default React.memo(ClientRescheduleError);
